/**
 * Wave 29 Phase F.1 — geolocation helper.
 *
 * Thin promise wrapper around `navigator.geolocation` that:
 *  • Asks the user for permission (browser-native).
 *  • Returns a normalized {lat, lng, accuracy_m, captured_at, denied,
 *    source} envelope ready to POST to /api/jobcards/{id} PUT.
 *  • Never rejects — denial / unsupported / timeout all resolve to
 *    `{denied: true}` so the JC start flow is never blocked by a GPS
 *    failure (per Wave 29 Q2: "allow + audit flag").
 */

const DEFAULT_TIMEOUT_MS = 12000;

const _now = () => new Date().toISOString();

const _envelope = (overrides = {}) => ({
  lat: null,
  lng: null,
  accuracy_m: null,
  denied: false,
  source: "browser",
  captured_at: _now(),
  ...overrides,
});

/**
 * Request the current position once.
 * @param {object} opts
 * @param {number} [opts.timeoutMs=12000]
 * @param {boolean} [opts.highAccuracy=true]
 * @returns {Promise<{lat, lng, accuracy_m, denied, source, captured_at, error?}>}
 *   Always resolves — never rejects.
 */
export function requestPosition({ timeoutMs = DEFAULT_TIMEOUT_MS, highAccuracy = true } = {}) {
  return new Promise((resolve) => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      resolve(_envelope({ denied: true, error: "geolocation_unsupported" }));
      return;
    }
    let settled = false;
    const t = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(_envelope({ denied: true, error: "geolocation_timeout" }));
    }, timeoutMs + 500);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        resolve(_envelope({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: Math.round(pos.coords.accuracy ?? 0),
        }));
      },
      (err) => {
        if (settled) return;
        settled = true;
        clearTimeout(t);
        // err.code 1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        resolve(_envelope({
          denied: true,
          error: err?.code === 1 ? "permission_denied"
            : err?.code === 2 ? "position_unavailable"
            : err?.code === 3 ? "geolocation_timeout"
            : "geolocation_error",
        }));
      },
      { enableHighAccuracy: highAccuracy, timeout: timeoutMs, maximumAge: 60000 },
    );
  });
}

/**
 * Format a distance in metres for the user-facing UI.
 *   12     → "12 m"
 *   480    → "480 m"
 *   2300   → "2.3 km"
 *   12000  → "12 km"
 */
export function formatDistance(metres) {
  if (metres == null || Number.isNaN(metres)) return "unknown";
  const m = Math.max(0, Math.round(metres));
  if (m < 1000) return `${m} m`;
  if (m < 10000) return `${(m / 1000).toFixed(1)} km`;
  return `${Math.round(m / 1000)} km`;
}
